import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Select from "react-select";
import { createSupervisorLog } from "../../services/supervisorService";
import { getProducts } from "../../services/productService";
import { AiOutlinePlus, AiOutlineClose, AiOutlineSave, AiOutlineClockCircle } from "react-icons/ai";
import { format } from 'date-fns';

const DailyLogForm = ({ showPopup }) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const [formData, setFormData] = useState({
        supervisor_id: user.id || 0,
        log_date: format(new Date(), 'yyyy-MM-dd'),
        shift: "Morning",
        notes: "",
        activities: []
    });

    const [products, setProducts] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // Predefined options for dropdowns
    const activityTypes = [
        { value: "Assembly", label: "Assembly" },
        { value: "Testing", label: "Testing" },
        { value: "Quality Control", label: "Quality Control" },
        { value: "Packaging", label: "Packaging" },
        { value: "Maintenance", label: "Maintenance" },
        { value: "Repair", label: "Repair" },
        { value: "Training", label: "Training" }
    ];

    const statusOptions = [
        { value: "Completed", label: "Completed ✓" },
        { value: "In Progress", label: "In Progress ⟳" },
        { value: "Delayed", label: "Delayed ⚠" },
        { value: "Cancelled", label: "Cancelled ✕" }
    ];

    const shiftOptions = [
        { value: "Morning", label: "Morning Shift (8AM - 4PM)" },
        { value: "Evening", label: "Evening Shift (4PM - 12AM)" },
        { value: "Night", label: "Night Shift (12AM - 8AM)" }
    ];

    useEffect(() => {
        fetchProducts();

        // Initialize with one empty activity
        if (formData.activities.length === 0) {
            addActivity();
        }
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await getProducts();
            let productOptions = [];

            if (Array.isArray(response)) {
                productOptions = response.map(product => ({
                    value: product.id,
                    label: product.name
                }));
            } else if (response && response.data && Array.isArray(response.data)) {
                productOptions = response.data.map(product => ({
                    value: product.id,
                    label: product.name
                }));
            }

            setProducts(productOptions);
        } catch (error) {
            console.error("Failed to fetch products:", error);
            showPopup("Failed to load products", "error");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleShiftChange = (selectedOption) => {
        setFormData({
            ...formData,
            shift: selectedOption.value
        });
    };

    const addActivity = () => {
        setFormData({
            ...formData,
            activities: [
                ...formData.activities,
                {
                    activity_type: "",
                    product_id: null,
                    quantity: 0,
                    worker_count: 1,
                    start_time: "",
                    end_time: "",
                    status: "Completed",
                    issues: ""
                }
            ]
        });
    };

    const removeActivity = (index) => {
        const activities = [...formData.activities];
        activities.splice(index, 1);

        setFormData({
            ...formData,
            activities
        });
    };

    const handleActivityChange = (index, field, value) => {
        const activities = [...formData.activities];
        activities[index][field] = value;

        setFormData({
            ...formData,
            activities
        });
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.log_date) {
            newErrors.log_date = "Date is required";
        }

        if (!formData.shift) {
            newErrors.shift = "Shift is required";
        }

        if (formData.activities.length === 0) {
            newErrors.activities = "At least one activity is required";
        } else {
            formData.activities.forEach((activity, index) => {
                if (!activity.activity_type) {
                    newErrors[`activity_${index}_type`] = "Activity type is required";
                }

                if (!activity.status) {
                    newErrors[`activity_${index}_status`] = "Status is required";
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            showPopup("Please fill in all required fields", "error");
            return;
        }

        setSubmitting(true);

        try {
            const response = await createSupervisorLog(formData);

            if (response && response.success) {
                showPopup("Daily log submitted successfully", "success");

                // Reset form
                setFormData({
                    supervisor_id: user.id || 0,
                    log_date: format(new Date(), 'yyyy-MM-dd'),
                    shift: "Morning",
                    notes: "",
                    activities: [{
                        activity_type: "",
                        product_id: null,
                        quantity: 0,
                        worker_count: 1,
                        start_time: "",
                        end_time: "",
                        status: "Completed",
                        issues: ""
                    }]
                });
            } else {
                showPopup("Failed to submit daily log", "error");
            }
        } catch (error) {
            console.error("Failed to submit log:", error);
            showPopup("Failed to submit daily log", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container>
            <Header>
                <h2>Daily Work Log</h2>
            </Header>

            <FormContainer onSubmit={handleSubmit}>
                <FormGroup>
                    <FormLabel htmlFor="log_date">Date:</FormLabel>
                    <FormInput
                        type="date"
                        id="log_date"
                        name="log_date"
                        value={formData.log_date}
                        onChange={handleInputChange}
                        isError={errors.log_date}
                    />
                    {errors.log_date && <ErrorText>{errors.log_date}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <FormLabel>Shift:</FormLabel>
                    <SelectWrapper isError={errors.shift}>
                        <Select
                            options={shiftOptions}
                            value={shiftOptions.find(option => option.value === formData.shift)}
                            onChange={handleShiftChange}
                            className="select-input"
                            classNamePrefix="select"
                            placeholder="Select shift"
                        />
                    </SelectWrapper>
                    {errors.shift && <ErrorText>{errors.shift}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <FormLabel htmlFor="notes">Notes:</FormLabel>
                    <TextArea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Any additional notes for the day..."
                        rows="3"
                    />
                </FormGroup>

                <ActivitiesSection>
                    <ActivitiesHeader>
                        <h3>Activities</h3>
                        <AddButton type="button" onClick={addActivity}>
                            <AiOutlinePlus />
                            Add Activity
                        </AddButton>
                    </ActivitiesHeader>

                    {errors.activities && <ErrorText>{errors.activities}</ErrorText>}

                    {formData.activities.map((activity, index) => (
                        <ActivityCard key={index}>
                            <ActivityHeader>
                                <ActivityTitle>Activity #{index + 1}</ActivityTitle>
                                {formData.activities.length > 1 && (
                                    <RemoveButton type="button" onClick={() => removeActivity(index)}>
                                        <AiOutlineClose />
                                    </RemoveButton>
                                )}
                            </ActivityHeader>

                            <ActivityGrid>
                                <FormGroup>
                                    <FormLabel>Activity Type:</FormLabel>
                                    <SelectWrapper isError={errors[`activity_${index}_type`]}>
                                        <Select
                                            options={activityTypes}
                                            value={activityTypes.find(option => option.value === activity.activity_type)}
                                            onChange={(selected) => handleActivityChange(index, "activity_type", selected.value)}
                                            className="select-input"
                                            classNamePrefix="select"
                                            placeholder="Select type"
                                        />
                                    </SelectWrapper>
                                    {errors[`activity_${index}_type`] && <ErrorText>{errors[`activity_${index}_type`]}</ErrorText>}
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel>Product:</FormLabel>
                                    <SelectWrapper>
                                        <Select
                                            options={products}
                                            value={products.find(option => option.value === activity.product_id)}
                                            onChange={(selected) => handleActivityChange(index, "product_id", selected ? selected.value : null)}
                                            className="select-input"
                                            classNamePrefix="select"
                                            placeholder="Select product"
                                            isClearable
                                        />
                                    </SelectWrapper>
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel htmlFor={`quantity-${index}`}>Quantity:</FormLabel>
                                    <FormInput
                                        type="number"
                                        id={`quantity-${index}`}
                                        value={activity.quantity}
                                        onChange={(e) => handleActivityChange(index, "quantity", parseInt(e.target.value) || 0)}
                                        min="0"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel htmlFor={`workers-${index}`}>Workers:</FormLabel>
                                    <FormInput
                                        type="number"
                                        id={`workers-${index}`}
                                        value={activity.worker_count}
                                        onChange={(e) => handleActivityChange(index, "worker_count", parseInt(e.target.value) || 1)}
                                        min="1"
                                    />
                                </FormGroup>

                                <FormGroup className="time-input">
                                    <FormLabel htmlFor={`start-time-${index}`}>Start Time:</FormLabel>
                                    <FormInput
                                        type="time"
                                        id={`start-time-${index}`}
                                        value={activity.start_time}
                                        onChange={(e) => handleActivityChange(index, "start_time", e.target.value)}
                                    />
                                </FormGroup>

                                <FormGroup className="time-input">
                                    <FormLabel htmlFor={`end-time-${index}`}>End Time:</FormLabel>
                                    <FormInput
                                        type="time"
                                        id={`end-time-${index}`}
                                        value={activity.end_time}
                                        onChange={(e) => handleActivityChange(index, "end_time", e.target.value)}
                                    />
                                </FormGroup>

                                <FormGroup className="status-select">
                                    <FormLabel>Status:</FormLabel>
                                    <SelectWrapper isError={errors[`activity_${index}_status`]}>
                                        <Select
                                            options={statusOptions}
                                            value={statusOptions.find(option => option.value === activity.status)}
                                            onChange={(selected) => handleActivityChange(index, "status", selected.value)}
                                            className="select-input"
                                            classNamePrefix="select"
                                            placeholder="Select status"
                                        />
                                    </SelectWrapper>
                                    {errors[`activity_${index}_status`] && <ErrorText>{errors[`activity_${index}_status`]}</ErrorText>}
                                </FormGroup>

                                <FormGroup className="full-width">
                                    <FormLabel htmlFor={`issues-${index}`}>Issues/Notes:</FormLabel>
                                    <TextArea
                                        id={`issues-${index}`}
                                        value={activity.issues || ""}
                                        onChange={(e) => handleActivityChange(index, "issues", e.target.value)}
                                        placeholder="Any issues or additional notes..."
                                        rows="2"
                                    />
                                </FormGroup>
                            </ActivityGrid>
                        </ActivityCard>
                    ))}
                </ActivitiesSection>

                <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit Log"}
                    <AiOutlineSave />
                </SubmitButton>
            </FormContainer>
        </Container>
    );
};

export default DailyLogForm;

// Styled Components
const Container = styled.div`
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    padding: 20px;
    margin-bottom: 20px;
`;

const Header = styled.div`
    margin-bottom: 20px;
    
    h2 {
        font-size: 18px;
        color: #333;
        margin: 0;
    }
`;

const FormContainer = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    &.full-width {
        grid-column: 1 / -1;
    }
    
    &.time-input {
        min-width: 140px;
    }
    
    &.status-select {
        min-width: 180px;
    }
`;

const FormLabel = styled.label`
    font-size: 14px;
    color: #555;
    font-weight: 500;
`;

const FormInput = styled.input`
    padding: 8px 12px;
    border: 1px solid ${props => props.isError ? '#f44336' : '#ddd'};
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const TextArea = styled.textarea`
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    resize: vertical;
    font-family: inherit;
    
    &:focus {
        outline: none;
        border-color: #2196F3;
    }
`;

const SelectWrapper = styled.div`
    .select-input {
        font-size: 14px;
    }
    
    .select__control {
        border-color: ${props => props.isError ? '#f44336' : '#ddd'};
        
        &:hover {
            border-color: ${props => props.isError ? '#f44336' : '#bbb'};
        }
        
        &.select__control--is-focused {
            border-color: #2196F3;
            box-shadow: 0 0 0 1px #2196F3;
        }
    }
    
    .select__menu {
        z-index: 100;
    }
`;

const ActivitiesSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const ActivitiesHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h3 {
        font-size: 16px;
        color: #333;
        margin: 0;
    }
`;

const ActivityCard = styled.div`
    background-color: #f9f9f9;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ActivityHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
`;

const ActivityTitle = styled.h4`
    font-size: 15px;
    color: #333;
    margin: 0;
`;

const ActivityGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
`;

const AddButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: #45a049;
    }
    
    svg {
        font-size: 16px;
    }
`;

const RemoveButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f5f5;
    color: #f44336;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
        background-color: #ffebee;
    }
    
    svg {
        font-size: 14px;
    }
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background-color: #000;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 12px 20px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    align-self: flex-start;
    margin-top: 10px;
    
    &:hover {
        background-color: #333;
    }
    
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
    
    svg {
        font-size: 18px;
    }
`;

const ErrorText = styled.div`
    color: #f44336;
    font-size: 12px;
    margin-top: -5px;
`;